import { Button, FileAnalysisBadges, FileThumbnail } from '@components'
import { getFilesPageUrl } from '@constants'
import { PermissionBoundary, PermissionSet, useBusinessContext } from '@context'
import { IFile, ISubFolder } from '@query'
import cn from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaEllipsisH, FaFolder, FaFolderOpen } from 'react-icons/fa'
import { FaInbox } from 'react-icons/fa6'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'

const FOLDER_ICON_SIZE = 24
const FILE_ICON_SIZE = 20
const LEADING_ICON_SLOT_SIZE = 24

interface Props {
  size: number
  folder: ISubFolder
  isOver: boolean
  isDragging: boolean
  isSelected: boolean
  isActiveTarget: boolean
  isFirst?: boolean
  isLast?: boolean
}

export const Folder: React.FC<Props> = ({
  folder,
  size,
  isDragging,
  isActiveTarget,
  isOver,
  isSelected,
  isFirst,
  isLast
}) => {
  const history = useHistory()
  const { slug } = useBusinessContext()
  const { t } = useTranslation()

  const folderName = useMemo(() => {
    if (folder.for_mobile_uploads) return t('files.browser.mobileUploads')
    return folder.name
  }, [folder])

  const folderClosedIcon = useMemo(() => {
    if (folder.for_mobile_uploads) return <FaInbox size={FOLDER_ICON_SIZE} />
    return <FaFolder size={FOLDER_ICON_SIZE} />
  }, [folder])

  const folderOpenIcon = useMemo(() => {
    return <FaFolderOpen size={FOLDER_ICON_SIZE} />
  }, [folder])

  return (
    <Draggable
      className={cn({
        'file-browser-item': true,
        'is-dragged': isDragging,
        'is-over': isOver,
        'is-selected': isSelected,
        'is-first': isFirst,
        'is-last': isLast
      })}
    >
      <StyledItem
        to={getFilesPageUrl(slug, { folder: folder.id, id: null, idType: null })}
        style={{ height: size }}
      >
        <LeadingIconSlot>{isActiveTarget ? folderOpenIcon : folderClosedIcon}</LeadingIconSlot>

        <ItemName>{folderName}</ItemName>

        <PermissionBoundary required={PermissionSet.FILES_EDIT}>
          <Button
            isSecondary={true}
            onClick={e => {
              history.push(
                getFilesPageUrl(slug, {
                  id: folder.id,
                  idType: 'folder',
                  folder: folder.parent
                })
              )
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <FaEllipsisH size={16} />
          </Button>
        </PermissionBoundary>
      </StyledItem>
    </Draggable>
  )
}

interface FileItemProps {
  size: number
  file: IFile
  isDragging: boolean
  didDrop: boolean
  isSelected: boolean
  isFirst?: boolean
  isLast?: boolean
}

export const FileItem: React.FC<FileItemProps> = ({
  file,
  size,
  isDragging,
  didDrop,
  isSelected,
  isFirst,
  isLast
}) => {
  const { slug } = useBusinessContext()
  const badges = file.analysis_badges || []

  return (
    <Draggable
      data-test="file-item"
      className={cn({
        'file-browser-item': true,
        'did-drop': didDrop,
        'is-selected': isSelected,
        'is-dragged': isDragging,
        'is-first': isFirst,
        'is-last': isLast
      })}
    >
      <StyledItem
        to={getFilesPageUrl(slug, { id: file.id, folder: file.folder_id, idType: 'file' })}
        style={{ height: size }}
      >
        <LeadingIconSlot>
          <FileThumbnail
            thumb={file.file}
            blurhash={file.blurhash}
            filetype={file.type}
            size={FILE_ICON_SIZE}
          />
        </LeadingIconSlot>
        <ItemMetaDataWrapper>
          <ItemName>{file.name}</ItemName>
          <FileAnalysisBadges badges={badges} fileId={file.id} alignEnd />
        </ItemMetaDataWrapper>
      </StyledItem>
    </Draggable>
  )
}

const Draggable = styled.div`
  border-radius: 0;
  overflow: hidden;
  opacity: 1;
  transition: 0.4s;
  background: white;

  &.is-dragged {
    opacity: 0.6;
    pointer-events: none;
  }

  &.did-drop {
    opacity: 0 !important;
  }

  &.is-over {
    cursor: copy;

    svg {
      transform: scale(1.2);
    }
  }

  &.is-selected {
    opacity: 0.6;
  }

  &.is-first {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &.is-last {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }
`

const StyledItem = styled(Link).attrs({
  onClick: e => {
    if (e.shiftKey || e.metaKey) {
      e.preventDefault()
    }
  }
})`
  display: flex;
  padding: ${({ theme }) => theme.spacing.md}rem;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  align-items: center;
  text-decoration: none !important;
  user-drag: none;

  &.isDragging {
    pointer-events: none;
  }

  svg {
    fill: ${({ theme }) => theme.colors.metalGray};
  }
`

const ItemName = styled.div`
  flex: 5 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const LeadingIconSlot = styled.span`
  width: ${LEADING_ICON_SLOT_SIZE}px;
  min-width: ${LEADING_ICON_SLOT_SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const ItemMetaDataWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`
